import React, { useState, useEffect, useCallback, useMemo } from 'react';
import OutfitAdminLayout from './OutfitAdminLayout';
import Modal from '../../global/Modal';
import { outfitAdminApi } from '../../../services/outfitAdminApi';

function KeywordsBlacklist({ email, onLogout }) {
  const [loading, setLoading] = useState(true);
  const [taxonomyData, setTaxonomyData] = useState({});
  const [summary, setSummary] = useState({ total_blacklisted_rules: 0, total_blacklisted_terms: 0 });
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Navigation / Selection State
  const [gender, setGender] = useState('men');
  const [superCategory, setSuperCategory] = useState('');
  const [category, setCategory] = useState('');

  // Draft Combination State
  const [draftTerms, setDraftTerms] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Delete Confirmation Modal State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState(null);

  const fetchTaxonomy = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await outfitAdminApi.fetchKeywordBlacklist();
      if (response && response.data) {
        setTaxonomyData(response.data);
        if (response.summary) {
          setSummary(response.summary);
        }
      }
    } catch (err) {
      console.error('Failed to load visual properties taxonomy:', err);
      setError(err.message || 'Failed to load visual properties taxonomy');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTaxonomy();
  }, [fetchTaxonomy]);

  // Available super categories for active gender
  const availableSuperCategories = useMemo(() => {
    if (!taxonomyData || !taxonomyData[gender]) return [];
    return Object.keys(taxonomyData[gender]);
  }, [taxonomyData, gender]);

  // Set default super category when gender or taxonomyData changes
  useEffect(() => {
    if (availableSuperCategories.length > 0) {
      if (!superCategory || !availableSuperCategories.includes(superCategory)) {
        setSuperCategory(availableSuperCategories[0]);
      }
    } else {
      setSuperCategory('');
    }
  }, [availableSuperCategories, superCategory]);

  // Available categories under active [gender][superCategory]
  const availableCategories = useMemo(() => {
    if (!taxonomyData || !taxonomyData[gender] || !superCategory || !taxonomyData[gender][superCategory]) {
      return [];
    }
    return Object.keys(taxonomyData[gender][superCategory]);
  }, [taxonomyData, gender, superCategory]);

  // Set default category when super category changes
  useEffect(() => {
    if (availableCategories.length > 0) {
      if (!category || !availableCategories.includes(category)) {
        setCategory(availableCategories[0]);
      }
    } else {
      setCategory('');
    }
  }, [availableCategories, category]);

  // Clear draft terms when category changes
  useEffect(() => {
    setDraftTerms([]);
  }, [gender, superCategory, category]);

  // Current category document
  const currentCategoryDoc = useMemo(() => {
    if (!taxonomyData || !taxonomyData[gender] || !superCategory || !category) return null;
    return taxonomyData[gender]?.[superCategory]?.[category] || null;
  }, [taxonomyData, gender, superCategory, category]);

  const activeBlacklistedRules = useMemo(() => {
    if (!currentCategoryDoc || !Array.isArray(currentCategoryDoc.blacklisted_items)) {
      return [];
    }
    return currentCategoryDoc.blacklisted_items;
  }, [currentCategoryDoc]);

  const propertiesObj = useMemo(() => {
    if (!currentCategoryDoc || !currentCategoryDoc.properties) return {};
    return currentCategoryDoc.properties;
  }, [currentCategoryDoc]);

  // Helper: Find which property dimension a term belongs to
  const getPropertyDimensionForTerm = (termValue) => {
    for (const [propName, valuesArr] of Object.entries(propertiesObj)) {
      const match = valuesArr.find((item) => {
        const val = typeof item === 'string' ? item : item.value;
        return val.toLowerCase() === termValue.toLowerCase();
      });
      if (match) return propName;
    }
    return null;
  };

  // Toggle or replace a value in the draft combination (enforcing max 1 value per dimension)
  const handleToggleTerm = (termValue, propertyDimension) => {
    setDraftTerms((prevDraft) => {
      // Find if we already have a term from this property dimension
      const filtered = prevDraft.filter((existingTerm) => {
        const existingDim = getPropertyDimensionForTerm(existingTerm);
        return existingDim !== propertyDimension;
      });

      // If user clicked the exact term already in draft, it gets removed
      const isAlreadySelected = prevDraft.includes(termValue);
      if (isAlreadySelected) {
        return filtered;
      }
      // Otherwise replace/add the term
      return [...filtered, termValue];
    });
  };

  const handleRemoveDraftChip = (termValue) => {
    setDraftTerms((prev) => prev.filter((t) => t !== termValue));
  };

  // Add combination to blacklist
  const handleAddBlacklist = async () => {
    if (draftTerms.length === 0) return;
    try {
      setSubmitting(true);
      setError('');
      setSuccessMsg('');

      await outfitAdminApi.addKeywordBlacklist({
        gender,
        super_category: superCategory,
        category,
        terms_containing: draftTerms,
      });

      setSuccessMsg(`Combination [${draftTerms.join(' + ')}] added to blacklist.`);
      setDraftTerms([]);
      await fetchTaxonomy();
    } catch (err) {
      console.error('Failed to add blacklist combination:', err);
      setError(err.message || 'Failed to add combination to blacklist');
    } finally {
      setSubmitting(false);
    }
  };

  // Open delete confirmation modal
  const handlePromptDelete = (rule) => {
    setRuleToDelete(rule);
    setDeleteModalOpen(true);
  };

  // Confirm delete rule
  const handleConfirmDelete = async () => {
    if (!ruleToDelete) return;
    try {
      setSubmitting(true);
      setError('');
      setSuccessMsg('');

      const terms = Array.isArray(ruleToDelete.value) ? ruleToDelete.value : [ruleToDelete.value];

      await outfitAdminApi.removeKeywordBlacklist({
        gender,
        super_category: superCategory,
        category,
        terms_containing: terms,
      });

      setSuccessMsg(`Rule [${terms.join(' + ')}] removed from blacklist.`);
      setDeleteModalOpen(false);
      setRuleToDelete(null);
      await fetchTaxonomy();
    } catch (err) {
      console.error('Failed to remove blacklist combination:', err);
      setError(err.message || 'Failed to remove rule from blacklist');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <OutfitAdminLayout
      email={email}
      onLogout={onLogout}
      pageTitle="Keywords Blacklist Management"
    >
      {error && (
        <div className="outfit-alert error">
          <span>{error}</span>
          <button className="outfit-btn outfit-btn-secondary" onClick={() => setError('')}>
            ✕
          </button>
        </div>
      )}

      {successMsg && (
        <div className="outfit-alert success">
          <span>{successMsg}</span>
          <button className="outfit-btn outfit-btn-secondary" onClick={() => setSuccessMsg('')}>
            ✕
          </button>
        </div>
      )}

      {/* Taxonomy Hierarchy Toolbar */}
      <div className="kb-hierarchy-bar">
        {/* Gender Switcher */}
        <div className="kb-gender-pills">
          <button
            className={`kb-gender-pill ${gender === 'men' ? 'active' : ''}`}
            onClick={() => setGender('men')}
          >
            Men
          </button>
          <button
            className={`kb-gender-pill ${gender === 'women' ? 'active' : ''}`}
            onClick={() => setGender('women')}
          >
            Women
          </button>
        </div>

        {/* Super Category Selector */}
        <div className="kb-selector-group">
          <label htmlFor="super-cat-select">Super Category:</label>
          <select
            id="super-cat-select"
            className="kb-select"
            value={superCategory}
            onChange={(e) => setSuperCategory(e.target.value)}
          >
            {availableSuperCategories.map((sc) => (
              <option key={sc} value={sc}>
                {sc.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </div>

        {/* Category Selector */}
        <div className="kb-selector-group">
          <label htmlFor="cat-select">Category:</label>
          <select
            id="cat-select"
            className="kb-select"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            {availableCategories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>

        {/* Ethnic tag indicator */}
        {currentCategoryDoc?.is_ethnic && (
          <span className="kb-ethnic-badge">Ethnic Wear</span>
        )}

        <div style={{ marginLeft: 'auto', fontSize: '13px', color: '#64748b' }}>
          Total Blacklisted Rules: <strong>{summary.total_blacklisted_rules}</strong>
        </div>
      </div>

      {loading ? (
        <div className="outfit-loading-state">
          <div className="outfit-spinner" />
          <p>Loading visual properties taxonomy...</p>
        </div>
      ) : (
        <>
          {/* Active Blacklisted Combinations Card */}
          <div className="outfit-card">
            <div className="outfit-card-header">
              <h2 className="outfit-card-title">
                Active Blacklisted Combinations for {category || 'Selected Category'}
              </h2>
              <span className="outfit-tab-badge">
                {activeBlacklistedRules.length} Rule{activeBlacklistedRules.length === 1 ? '' : 's'}
              </span>
            </div>

            {activeBlacklistedRules.length === 0 ? (
              <div className="outfit-empty-state" style={{ padding: '30px 20px' }}>
                <p>No blacklisted combinations configured for this category yet.</p>
              </div>
            ) : (
              <div className="kb-rules-list">
                {activeBlacklistedRules.map((rule, idx) => {
                  const terms = Array.isArray(rule.value) ? rule.value : [rule.value];
                  return (
                    <div key={idx} className="kb-rule-row">
                      <div className="kb-rule-terms">
                        {terms.map((t, tIdx) => (
                          <React.Fragment key={tIdx}>
                            <span className="kb-rule-term-badge">{t}</span>
                            {tIdx < terms.length - 1 && <span className="kb-rule-plus">+</span>}
                          </React.Fragment>
                        ))}
                      </div>

                      <div className="kb-rule-meta">
                        {rule.number_of_item_id !== undefined && (
                          <span className="kb-item-count-pill">
                            {rule.number_of_item_id} items impacted
                          </span>
                        )}
                        <button
                          className="kb-delete-btn"
                          title="Remove Rule"
                          onClick={() => handlePromptDelete(rule)}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Combination Builder Box */}
          <div className="kb-builder-box">
            <div className="kb-builder-header">Create New Blacklist Combination</div>
            <div className="kb-builder-content">
              <div className="kb-draft-chips">
                {draftTerms.length === 0 ? (
                  <span style={{ fontSize: '13px', color: '#94a3b8' }}>
                    Select properties from the taxonomy below (max 1 value per dimension)...
                  </span>
                ) : (
                  draftTerms.map((term, idx) => (
                    <span key={idx} className="kb-draft-chip">
                      {term}
                      <button
                        className="kb-draft-chip-remove"
                        onClick={() => handleRemoveDraftChip(term)}
                        title="Remove term"
                      >
                        ✕
                      </button>
                    </span>
                  ))
                )}
              </div>

              <div className="kb-builder-actions">
                <button
                  className="outfit-btn outfit-btn-primary"
                  disabled={draftTerms.length === 0 || submitting}
                  onClick={handleAddBlacklist}
                >
                  {submitting ? 'Adding...' : '+ Add Combination to Blacklist'}
                </button>
              </div>
            </div>
          </div>

          {/* Visual Properties Taxonomy Grid */}
          <div className="outfit-card">
            <div className="outfit-card-header">
              <h2 className="outfit-card-title">Visual Properties Taxonomy</h2>
              <span style={{ fontSize: '12px', color: '#64748b' }}>
                Click 1 value per property to compose a blacklist combination
              </span>
            </div>

            <div className="kb-taxonomy-grid">
              {Object.entries(propertiesObj).map(([propKey, valuesArr]) => (
                <div key={propKey} className="kb-property-card">
                  <div className="kb-property-title">{propKey}</div>
                  <div className="kb-values-list">
                    {valuesArr.map((vItem, vIdx) => {
                      const val = typeof vItem === 'string' ? vItem : vItem.value;
                      const isSelected = draftTerms.includes(val);
                      return (
                        <button
                          key={vIdx}
                          className={`kb-value-chip ${isSelected ? 'selected' : ''}`}
                          onClick={() => handleToggleTerm(val, propKey)}
                        >
                          {val}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Delete Rule Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Remove Blacklist Rule"
      >
        <div style={{ padding: '8px 0' }}>
          <p style={{ fontSize: '14px', color: '#334155', lineHeight: '1.5' }}>
            Are you sure you want to remove this blacklist combination for{' '}
            <strong>{gender} &gt; {superCategory} &gt; {category}</strong>?
          </p>
          {ruleToDelete && (
            <div style={{ margin: '16px 0', padding: '12px', background: '#f8fafc', borderRadius: '6px' }}>
              <strong>Rule:</strong>{' '}
              {(Array.isArray(ruleToDelete.value) ? ruleToDelete.value : [ruleToDelete.value]).join(' + ')}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' }}>
            <button
              className="outfit-btn outfit-btn-secondary"
              onClick={() => setDeleteModalOpen(false)}
            >
              Cancel
            </button>
            <button
              className="outfit-btn outfit-btn-danger"
              disabled={submitting}
              onClick={handleConfirmDelete}
            >
              {submitting ? 'Removing...' : 'Confirm Remove'}
            </button>
          </div>
        </div>
      </Modal>
    </OutfitAdminLayout>
  );
}

export default KeywordsBlacklist;
